import api from './api';

export interface User {
  id: string;
  // The plants the user works in; empty for every plant
  plantIds?: string[];
  // For a portal account: the customer or vendor it belongs to
  portalCustomerId?: string | null;
  portalVendorId?: string | null;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  fullName?: string;
  phone?: string;
  mobile?: string;
  department?: string;
  designation?: string;
  employeeId?: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  userType: 'internal' | 'external' | 'system';
  role: Role;
  profilePicture?: string;
  preferences?: Record<string, any>;
  permissions?: string[];
  lastLoginAt?: string;
  // Set while the account is locked after failed logins
  lockedUntil?: string | null;
  // Set on deleted users
  deletedAt?: string | null;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  permissions: string[];
  moduleAccess?: Record<string, any>;
  priority: number;
  isActive: boolean;
  isSystem: boolean;
}

export interface Permission {
  id: string;
  code: string;
  name: string;
  description?: string;
  module: string;
  action: string;
  resource?: string;
  isActive: boolean;
}

export interface UserActivity {
  id: string;
  activityType: string;
  module: string;
  entityType?: string;
  entityId?: string;
  description?: string;
  ipAddress?: string;
  // The browser the action came from
  userAgent?: string;
  createdAt: string;
}

export interface CreateUserDto {
  email: string;
  plantIds?: string[];
  portalCustomerId?: string | null;
  portalVendorId?: string | null;
  // Made from the email when left out
  username?: string;
  password: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  phone?: string;
  mobile?: string;
  department?: string;
  designation?: string;
  employeeId?: string;
  roleId: string;
  status?: 'active' | 'inactive' | 'suspended' | 'pending';
  userType?: 'internal' | 'external' | 'system';
  permissions?: string[];
  preferences?: Record<string, any>;
  sendWelcomeEmail?: boolean;
  requireEmailVerification?: boolean;
}

export interface UpdateUserDto {
  email?: string;
  plantIds?: string[];
  portalCustomerId?: string | null;
  portalVendorId?: string | null;
  username?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  phone?: string;
  mobile?: string;
  department?: string;
  designation?: string;
  employeeId?: string;
  roleId?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'pending';
  userType?: 'internal' | 'external' | 'system';
  permissions?: string[];
  preferences?: Record<string, any>;
  profilePicture?: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UserQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  userType?: string;
  roleId?: string;
  department?: string;
  emailVerified?: boolean;
  includeDeleted?: boolean;
  onlyDeleted?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface CreateRoleDto {
  name: string;
  displayName: string;
  description?: string;
  permissions: string[];
  moduleAccess?: Record<string, any>;
  priority?: number;
  isActive?: boolean;
  isSystem?: boolean;
}

export interface UserPage {
  data: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UserSummary {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  inactive: number;
  // Locked out after too many failed logins
  locked: number;
  deleted: number;
  // Departments in use, for the filter
  departments: string[];
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

class UserService {
  async getUsers(params: UserQueryParams = {}): Promise<UserPage> {
    const response = await api.get('/users', { params: clean(params) });
    return response.data;
  }

  async getSummary(): Promise<UserSummary> {
    const response = await api.get('/users/summary');
    return response.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserDto): Promise<User> {
    const response = await api.post('/users', data);
    return response.data;
  }

  async updateUser(id: string, data: UpdateUserDto): Promise<User> {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  }

  async restoreUser(id: string): Promise<User> {
    const response = await api.post(`/users/${id}/restore`);
    return response.data;
  }

  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    await api.post(`/users/${id}/reset-password`, { newPassword });
  }

  // Turns two-step sign-in off for someone who lost their phone; they set it up again themselves
  async resetTwoFactor(id: string): Promise<void> {
    await api.post(`/users/${id}/reset-two-factor`);
  }

  async getCurrentUserProfile(): Promise<User> {
    const response = await api.get('/users/profile');
    return response.data;
  }

  // Your own activity; pass kinds such as ['login', 'failed_login'] to get only those
  async getMyActivities(limit = 20, types?: string[]): Promise<UserActivity[]> {
    const response = await api.get('/users/activities', { params: { limit, ...(types?.length ? { types: types.join(',') } : {}) } });
    return response.data;
  }

  async updateCurrentUserProfile(data: UpdateUserDto): Promise<User> {
    const response = await api.patch('/users/profile', data);
    return response.data;
  }

  async changePassword(data: ChangePasswordDto): Promise<void> {
    await api.post('/users/change-password', data);
  }

  async getUserActivities(userId?: string, limit?: number): Promise<UserActivity[]> {
    const url = userId 
      ? `/users/${userId}/activities` 
      : '/users/activities';
    const response = await api.get(url, { params: { limit } });
    return response.data;
  }

  // Role management
  async getRoles(): Promise<Role[]> {
    const response = await api.get('/roles');
    return response.data;
  }

  async getRoleById(id: string): Promise<Role> {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  }

  async createRole(data: CreateRoleDto): Promise<Role> {
    const response = await api.post('/roles', data);
    return response.data;
  }

  async updateRole(id: string, data: CreateRoleDto): Promise<Role> {
    const response = await api.patch(`/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await api.delete(`/roles/${id}`);
  }

  async getAllPermissions(): Promise<Permission[]> {
    const response = await api.get('/roles/permissions');
    return response.data;
  }

  async initializeRolesAndPermissions(): Promise<void> {
    await api.post('/roles/initialize');
  }
}

const userService = new UserService();
export default userService;